import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Terminal, Code2, Blocks, Send, Search, CheckCircle2, XCircle, Shield, Wallet } from 'lucide-react';

const MatrixRain = () => (
  <div className="fixed inset-0 bg-black opacity-30 pointer-events-none">
    <div className="matrix-rain text-emerald-500 text-xl font-mono animate-matrix pointer-events-none">
      {Array.from({ length: 50 }).map((_, i) => (
        <div key={i} className="matrix-column pointer-events-none" style={{ animationDelay: `${Math.random() * 2}s` }}>
          {Array.from({ length: 30 }).map((_, j) => (
            <span key={j} style={{ animationDelay: `${Math.random() * 5}s` }} className="pointer-events-none">
              {String.fromCharCode(33 + Math.random() * 93)}
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const AnimatedLogo = () => {
  useEffect(() => {
    const textElement = document.getElementById("animatedText");
    function restartAnimation() {
      if (textElement) {
        textElement.style.transition = "none";
          textElement.style.strokeDashoffset = "1000";
        setTimeout(() => {
          textElement.style.transition = "stroke-dashoffset 2s ease-in-out";
          textElement.style.strokeDashoffset = "0";
        }, 50);
      }
    }
    restartAnimation();
    const interval = setInterval(restartAnimation, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <svg 
        viewBox="0 0 400 100" 
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
        style={{ minHeight: '40px', maxHeight: '64px' }}
      >
        <defs>
          <linearGradient id="strokeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0fa" />
            <stop offset="50%" stopColor="#0ff" />
            <stop offset="100%" stopColor="#0fa" />
          </linearGradient>
        </defs>
        <text
          id="animatedText"
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="font-['Fira_Code']"
          style={{
            fill: 'rgba(0, 0, 0, 0.95)',
            stroke: '#10B981',
            strokeWidth: '2px',
            strokeDasharray: '1000',
            strokeDashoffset: '1000',
            fontSize: '42px',
            fontFamily: "'Fira Code', 'Consolas', monospace",
            fontWeight: 'bold',
            letterSpacing: '2px'
          }}
        >
          {"SOLBVR<DEVS>"}
        </text>
      </svg>
    </div>
  );
};

const AnimatedSectionTitle = ({ children }) => {
  const id = `section-title-${children.replace(/[^a-zA-Z0-9]/g, '')}`;
  
  useEffect(() => {
    const textElement = document.getElementById(id);
    function restartAnimation() {
      if (textElement) {
        textElement.style.transition = "none";
        textElement.style.strokeDashoffset = "1000";
        setTimeout(() => {
          textElement.style.transition = "stroke-dashoffset 2s ease-in-out";
          textElement.style.strokeDashoffset = "0";
        }, 50);
      }
    }
    restartAnimation();
    const interval = setInterval(restartAnimation, 4000);
    return () => clearInterval(interval);
  }, [id]);

  return (
    <div className="text-center my-4">
      <svg 
        viewBox="0 0 400 100" 
        className="w-full max-w-lg mx-auto"
        preserveAspectRatio="xMidYMid meet"
        style={{ height: '80px' }}
      >
        <defs>
          <linearGradient id={`strokeGradient-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0fa" />
            <stop offset="50%" stopColor="#0ff" />
            <stop offset="100%" stopColor="#0fa" />
          </linearGradient>
        </defs>
        <text
          id={id}
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="font-['Fira_Code']"
          style={{
            fill: 'rgba(0, 0, 0, 0.95)',
            stroke: '#10B981',
            strokeWidth: '2px',
            strokeDasharray: '1000',
            strokeDashoffset: '1000',
            fontSize: '42px',
            fontFamily: "'Fira Code', 'Consolas', monospace",
            fontWeight: 'bold',
            letterSpacing: '2px'
          }}
        >
          {children}
        </text>
      </svg>
    </div>
  );
};

const AnimatedTitle = ({ children }) => {
  return (
    <div className="text-center my-4">
      <h2 className="text-3xl md:text-4xl font-bold text-emerald-500 font-mono animate-pulse">
        {children}
      </h2>
    </div>
  );
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMenuOpen) setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);

    const handleResize = () => {
      if (window.innerWidth > 768 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
  }, [isMenuOpen]);

  const navItems = [
    { name: 'Services', icon: <Terminal className="w-4 h-4" /> },
    { name: 'Solutions', icon: <Blocks className="w-4 h-4" /> },
    { name: 'About', icon: <Code2 className="w-4 h-4" /> },
    { name: 'Quote', icon: <Wallet className="w-4 h-4" />, href: '#contact' },
    { 
      name: '', 
      icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>,
      href: 'https://x.com/thesolbeavers',
      external: true
    },
    { 
      name: '', 
      icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
      </svg>,
      href: 'https://discord.gg/VGdgKP49Nh',
      external: true
    }
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 backdrop-blur-lg border-b border-emerald-500/10 ${
        isScrolled ? 'py-3 bg-black' : 'py-5 bg-transparent'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <motion.div 
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="flex items-center justify-start w-48 md:w-64 h-16"
            >
              <AnimatedLogo />
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <motion.a
                  key={`${item.name || item.href}-${index}`}
                  href={item.external ? item.href : item.href || `#${item.name.toLowerCase().replace(' ', '-')}`}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className="relative group flex items-center space-x-2 text-white/80 hover:text-white transition-colors mx-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.icon}
                  <span className="font-mono">{item.name}</span>
                  <span className="absolute -top-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-600 group-hover:w-full transition-all duration-300"></span>
                  <span className="absolute -bottom-1 right-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-600 group-hover:w-full transition-all duration-300"></span>
                </motion.a>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden w-10 h-10 flex flex-col justify-center items-center z-50"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className={`w-5 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'}`}></span>
              <span className={`w-5 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`w-5 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : 'translate-y-1'}`}></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          isMenuOpen ? 'opacity-100 visible z-40' : 'opacity-0 invisible'
        }`}
        onClick={() => setIsMenuOpen(false)}
      ></div>

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 w-full max-w-[280px] h-full bg-gradient-to-br from-black to-emerald-950 backdrop-blur-lg transform transition-transform duration-300 ease-in-out md:hidden z-50 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full pt-20 pb-6 px-6">
          <div className="flex-1 flex flex-col items-start justify-start space-y-6">
            {navItems.map((item, index) => (
              <motion.a
                key={`mobile-${item.name || item.href}-${index}`}
                href={item.external ? item.href : item.href || `#${item.name.toLowerCase().replace(' ', '-')}`}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                className="flex items-center space-x-3 text-white/80 hover:text-white transition-colors w-full"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: isMenuOpen ? 1 : 0, x: isMenuOpen ? 0 : 20 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.icon}
                <span className="font-mono text-lg">{item.name}</span>
              </motion.a>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          animation: gradient 8s linear infinite;
        }
        .bg-300\\% {
          background-size: 300%;
        }
      `}</style>
    </>
  );
};

const Hero = () => {
  const [text, setText] = useState('');
  const fullText = 'Building Web3 Future';

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div id="hero" className="min-h-screen flex items-center justify-center relative px-4">
      <div className="text-center z-10">
        <motion.h1 
          className="text-4xl md:text-6xl font-bold text-white mb-6 font-mono break-words"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <AnimatedLogo />
          {text}
          <span className="animate-pulse">|</span>
        </motion.h1>
        <motion.p
          className="text-xl text-white font-bold mb-8 font-mono"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          Blockchain • Web3 • Development
        </motion.p>
        <motion.button
          className="bg-emerald-500 text-white px-8 py-3 rounded-full hover:bg-emerald-600 transition-colors font-mono"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={scrollToServices}
        >
          Explore Services
        </motion.button>
      </div>
    </div>
  );
};

const ServiceModal = ({ service, onClose, isOpen }) => {
  if (!isOpen || !service) return null;
  const detailedInfo = {
    'Blockchain Solutions': {
      description: "Enterprise-grade blockchain solutions with a focus on security, scalability, and interoperability.",
      services: [
        "Custom Blockchain Implementation",
        "Cross-chain Integration Solutions",
        "Smart Contract Development",
        "Blockchain Security Analysis"
      ],
      technologies: ["React.js", "Node.js", "IPFS", "MetaMask SDK", "The Graph", "Chainlink"]
    },
    'Web3 Development': {
      description: "Cutting-edge Web3 solutions focusing on decentralization and user empowerment.",
      services: [
        "DeFi Protocol Development",
        "NFT Platform Creation",
        "DAO Infrastructure",
        "Web3 Security Solutions"
      ],
      technologies: ["Solidity", "Rust", "Web3.js", "Ethers.js", "IPFS", "Hardhat"]
    },
    'Technical Solutions': {
      description: "Full-stack Web2 & Web3 development solutions, including secure VPS hosting and robust backend systems",
      services: [
        "Frontend & Backend Development",
        "VPS Security Hardening",
        "Legacy & Modern System Integration",
        "Smart Contract Testing",
        "Technical Documentation"
      ],
      technologies: ["TypeScript", "React", "Node.js", "PostgreSQL", "Redis", "AWS"]
    },
    'Security & Auditing': {
      description: "Fortify your smart contracts with next-gen audits, cutting-edge fuzzing, and comprehensive security reviews - the latest & greatest, degen style!",
      services: [
        "Smart Contract Auditing",
        "Vulnerability Assessment",
        "Penetration Testing",
        "Compliance Reviews"
      ],
      technologies: ["Diligence Fuzzing", "Slither", "Oyente", "Manticore"]
    }
  };

  const info = detailedInfo[service.title];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black backdrop-blur-sm overflow-hidden"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-800 p-4 md:p-8 rounded-lg border-2 border-emerald-500 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center space-x-4">
            {service.icon}
            <h3 className="text-xl md:text-2xl font-bold text-emerald-400 font-mono truncate">
              {service.title}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="space-y-6">
          <div>
            <p className="text-gray-300 mb-4 font-mono">{info.description}</p>
          </div>
          <div>
            <h4 className="text-emerald-400 font-mono mb-2">Key Features</h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {info.services.map((feature, index) => (
                <li key={index} className="flex items-center space-x-2 text-gray-300 font-mono">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-emerald-400 font-mono mb-2">Technologies</h4>
            <div className="flex flex-wrap gap-2">
              {info.technologies.map((tech, index) => (
                <span key={index} className="px-3 py-1 bg-emerald-900 text-emerald-400 rounded-full text-sm font-mono">
                  {tech}
                </span>
              ))}
            </div>
          </div>
          </div>
        <div className="mt-8 flex justify-end">
          <button onClick={onClose} className="bg-emerald-500 text-white px-6 py-2 rounded-lg font-mono hover:bg-emerald-600 transition-colors">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      title: "Blockchain Solutions",
      description: "Enterprise-grade blockchain infrastructure",
      icon: <Blocks className="w-12 h-12 text-emerald-500" />,
      features: [
        "Multi-chain Integration",
        "Smart Contract Development",
        "DeFi Protocol Implementation",
        "Cross-chain Bridges"
      ]
    },
    {
      title: "Web3 Development",
      description: "Full-stack Web3 applications and infrastructure",
      icon: <Wallet className="w-12 h-12 text-emerald-500" />,
      features: [
        "dApp Development",
        "NFT Platforms",
        "DAO Infrastructure",
        "IPFS Integration"
      ]
    },
    {
      title: "Technical Solutions",
      description: "Comprehensive development and infrastructure services",
      icon: <Terminal className="w-12 h-12 text-emerald-500" />,
      features: [
        "Frontend Development",
        "Backend Architecture",
        "Smart Contract Testing",
        "Technical Documentation"
      ]
    },
    {
      title: "Security & Auditing",
      description: "Fortify your smart contracts with next-gen audits, cutting-edge fuzzing, and comprehensive security reviews - the latest & greatest, degen style!",
      icon: <Shield className="w-12 h-12 text-emerald-500" />,
      features: [
        "Smart Contract Auditing",
        "Vulnerability Assessment",
        "Penetration Testing",
        "Compliance Reviews"
      ]
    }
  ];

  return (
    <div id="services" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <AnimatedSectionTitle>SERVICES</AnimatedSectionTitle>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 p-4 md:p-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="bg-transparent p-6 rounded-lg border-2 border-emerald-500 shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:bg-gray-700/50"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              onClick={() => service.link ? window.location.href = service.link : setSelectedService(service)}
            >
              <div className="flex flex-col items-center text-center">
                {service.icon}
                <h3 className="text-xl font-bold text-emerald-500 mb-4 font-mono">{service.title}</h3>
                <ul className="text-gray-300 space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="font-mono">{feature}</li>
                  ))}
                </ul>
                {service.link && (
                  <motion.button
                    className="mt-4 px-4 py-2 bg-emerald-500 text-black rounded-lg font-mono hover:bg-emerald-400 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Launch App
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <ServiceModal
        service={selectedService}
        isOpen={!!selectedService}
        onClose={() => setSelectedService(null)}
      />
    </div>
  );
};

const SolutionModal = ({ solution, onClose, isOpen }) => {
  if (!isOpen || !solution) return null;
  
  const detailedInfo = {
    'Blockchain Solutions': {
      description: "Enterprise-grade blockchain infrastructure with cutting-edge security and scalability.",
      services: [
        "Custom Smart Contract Development",
        "Multi-chain Protocol Integration",
        "NFT Platform Development",
        "DeFi Protocol Implementation"
      ],
      technologies: ["Solidity", "Rust", "Web3.js", "Ethers.js", "The Graph", "Chainlink"]
    },
    'Web3 Infrastructure': {
      description: "Comprehensive decentralized application architecture and infrastructure solutions.",
      services: [
        "IPFS Storage Implementation",
        "Cross-chain Bridge Development",
        "Wallet Integration Services",
        "Security Audit & Testing"
      ],
      technologies: ["IPFS", "MetaMask SDK", "WalletConnect", "Hardhat", "Truffle", "OpenZeppelin"]
    },
    'DeFi Development': {
      description: "Advanced decentralized finance solutions with robust security measures.",
      services: [
        "Token Smart Contract Development",
        "Staking Platform Creation",
        "Yield Optimization Protocols",
        "DEX Integration & Development"
      ],
      technologies: ["Solidity", "Web3.js", "Uniswap SDK", "1inch API", "Aave Protocol", "Compound Protocol"]
    }
  };

  const info = detailedInfo[solution.title];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black backdrop-blur-sm overflow-hidden"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-800 p-4 md:p-8 rounded-lg border-2 border-emerald-500 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center space-x-4">
            {solution.icon}
            <h3 className="text-xl md:text-2xl font-bold text-emerald-400 font-mono truncate">
              {solution.title}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="space-y-6">
          <div>
            <p className="text-gray-300 mb-4 font-mono">{info.description}</p>
          </div>
          <div>
            <h4 className="text-emerald-400 font-mono mb-2">Key Features</h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {info.services.map((feature, index) => (
                <li key={index} className="flex items-center space-x-2 text-gray-300 font-mono">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-emerald-400 font-mono mb-2">Technologies</h4>
            <div className="flex flex-wrap gap-2">
              {info.technologies.map((tech, index) => (
                <span key={index} className="px-3 py-1 bg-emerald-900 text-emerald-400 rounded-full text-sm font-mono">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-end">
          <button onClick={onClose} className="bg-emerald-500 text-white px-6 py-2 rounded-lg font-mono hover:bg-emerald-600 transition-colors">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Solutions = () => {
  const [selectedSolution, setSelectedSolution] = useState(null);

  const solutions = [
    {
      title: "Blockchain Solutions",
      description: "Enterprise-grade blockchain infrastructure",
      features: [
        "Custom Smart Contracts",
        "Multi-chain Integration",
        "NFT Marketplaces",
        "DeFi Protocols"
      ],
      icon: <Blocks className="h-12 w-12 text-emerald-400" />
    },
    {
      title: "Web3 Infrastructure",
      description: "Decentralized application architecture",
      features: [
        "IPFS Storage Systems",
        "Cross-chain Bridges",
        "Wallet Integration",
        "Security & Audits"  
      ],
      icon: <Terminal className="h-12 w-12 text-emerald-400" />
    },
    {
      title: "DeFi Development",
      description: "Decentralized finance solutions",
      features: [
        "Token Development",
        "Staking Platforms",
        "Yield Optimization",
        "DEX Integration"
      ],
      icon: <Wallet className="h-12 w-12 text-emerald-400" />
    }
  ];

  return (
    <div id="solutions" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <AnimatedSectionTitle>[SOLUTIONS]</AnimatedSectionTitle>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 p-4 md:p-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              className="bg-transparent p-6 rounded-lg border-2 border-emerald-500 shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:bg-gray-700/50"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              onClick={() => setSelectedSolution(solution)}
            >
              <div className="flex flex-col items-center text-center">
                <div className="mb-6">
                  {solution.icon}
                </div>
                <h3 className="text-2xl font-bold text-emerald-400 mb-4">{solution.title}</h3>
                <p className="text-white mb-6">{solution.description}</p>
                <ul className="text-white space-y-3">
                  {solution.features.map((feature) => (
                    <li key={feature} className="flex items-center justify-center">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <SolutionModal
        solution={selectedSolution}
        isOpen={!!selectedSolution}
        onClose={() => setSelectedSolution(null)}
      />
    </div>
  );
};

const About = () => {
  const team = [
    {
      name: "SAMGAD",
      role: "Lead Backend Developer",
      description: "Architecting robust backend solutions and blockchain infrastructure",
      image: "https://ipfs.io/ipfs/bafkreia5mzwnixtooajbyya65mzrzeyzkgnxoneqcjeomhjirge6622wfi",
    },
    {
      name: "FAWL3R",
      role: "Lead UI/UX & Frontend Developer",
      description: "Crafting beautiful user experiences and intuitive interfaces",
      image: "https://ipfs.io/ipfs/bafkreic5cx43w6nmsmq2nqqpwrfmd6pxydhlg3fswpvwws3ivj5dey3uzm",
    },
    {
      name: "OBZ",
      role: "Lead Marketing",
      description: "Driving growth and community engagement strategies",
      image: "https://ipfs.io/ipfs/bafkreiadzwunjt3ur5tbjvnjfev4bpp4gobd7ez2hlegczuxlez2ps7ksm",
    },
    {
      name: "PEPERONCINO",
      role: "Lead Manager",
      description: "Orchestrating team success and project delivery",
      image: "https://ipfs.io/ipfs/bafkreiao7b3qosdyrgm4ztxcmmiia3e5t7avxaewix72p6nkbxa3gafr6q",
    }
  ];

  return (
    <div id="about" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <AnimatedSectionTitle>&lt;TEAM/&gt;</AnimatedSectionTitle>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 p-4 md:p-8">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              className="bg-gray-800 rounded-lg overflow-hidden border-2 border-emerald-500"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="aspect-square w-full overflow-hidden bg-emerald-900">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-emerald-400 mb-2 font-mono">{member.name}</h3>
                <p className="text-emerald-300 text-sm mb-3 font-mono">{member.role}</p>
                <p className="text-gray-300 text-sm font-mono">{member.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const QuoteSection = () => {
  const quotes = useMemo(() => [
    "Building the decentralized future, one block at a time!",
    "Web3 is not just technology - it's a revolution in trust and transparency!",
    "Smart contracts are the building blocks of a more equitable digital world!",
    "Decentralization is the key to true digital ownership and freedom!",
    "In Web3, every user is an owner, every transaction is transparent!"
  ], []);

  const [quote, setQuote] = useState(quotes[0]);

  useEffect(() => {
    const interval = setInterval(() => {
      const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
      setQuote(randomQuote);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full overflow-hidden bg-black/50 backdrop-blur-sm border-t border-b border-emerald-500/10 py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="max-w-3xl">
            <p className="text-lg md:text-2xl text-emerald-400 font-mono leading-relaxed italic">
              {quote}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="bg-black border-t border-emerald-500">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="space-y-4">
            <h3 className="text-emerald-400 font-mono text-lg font-bold">Dev.solbvr</h3>
            <p className="text-gray-400 font-mono text-sm">
              Building the decentralized future, one block at a time.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-emerald-400 font-mono text-lg font-bold">Services</h3>
            <ul className="space-y-2 text-gray-400 font-mono text-sm">
              <li>
                <a href="#services" className="hover:text-emerald-400 transition-colors">
                  Blockchain Development
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-emerald-400 transition-colors">
                  Smart Contracts
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-emerald-400 transition-colors">
                  Web3 Integration
                </a>
              </li>
              <li>
                <a href="#services" className="hover:text-emerald-400 transition-colors">
                  DeFi Solutions
                </a>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-emerald-400 font-mono text-lg font-bold">Resources</h3>
            <ul className="space-y-2 text-gray-400 font-mono text-sm">
              <li>
                <a href="#token-sniffer" className="hover:text-emerald-400 transition-colors">
                  CNTCT//SNIFR
                </a>
              </li>
              <li>
                <a href="#beaver-bot" className="hover:text-emerald-400 transition-colors">
                  BVR//BOT
                </a>
              </li>
              <li>
                <a 
                  href="https://x.com/thesolbeavers" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-emerald-400 transition-colors"
                >
                  Twitter
                </a>
              </li>
              <li>
                <a 
                  href="https://discord.gg/VGdgKP49Nh" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-emerald-400 transition-colors"
                >
                  Discord
                </a>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-emerald-400 font-mono text-lg font-bold">Connect</h3>
            <ul className="space-y-2 text-gray-400 font-mono text-sm">
              <li>
                <a 
                  href="https://x.com/thesolbeavers" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <span>Twitter</span>
                  <span className="text-xs text-emerald-500">@thesolbeavers</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://discord.gg/VGdgKP49Nh" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-emerald-400 transition-colors"
                >
                  Discord
                </a>
              </li>
              <li>
                <a 
                  href="https://t.me/c/2413133102/1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-emerald-400 transition-colors flex items-center gap-2"
                >
                  <span>Telegram</span>
                  <span className="text-xs text-emerald-500">@solbvr</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-emerald-500 mt-12 pt-8 text-center">
          <p className="text-gray-400 font-mono text-sm">
            &copy; {new Date().getFullYear()} Dev.solbvr. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [lastSubmission, setLastSubmission] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const COOLDOWN_TIME = 60000; // 1 minute cooldown

  useEffect(() => {
    if (remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime(prev => Math.max(0, prev - 1000));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [remainingTime]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmission;
    
    if (timeSinceLastSubmission < COOLDOWN_TIME) {
      const remaining = Math.ceil((COOLDOWN_TIME - timeSinceLastSubmission) / 1000);
      setRemainingTime(COOLDOWN_TIME - timeSinceLastSubmission);
      alert(`Please wait ${remaining} seconds before submitting again.`);
      return;
    }

    const mailtoLink = `mailto:beaversonsol@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    )}`;
    
    setLastSubmission(now);
    setRemainingTime(COOLDOWN_TIME);
    
    window.location.href = mailtoLink;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div id="contact" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <AnimatedSectionTitle>CONTACT US</AnimatedSectionTitle>
        </motion.div>
        <motion.p
          className="text-emerald-400 text-center mb-12 font-mono"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Get in touch with our team
        </motion.p>

        <motion.div
          className="max-w-2xl mx-auto"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
        >
          <form onSubmit={handleSubmit} className="bg-gray-800 p-6 rounded-lg border-2 border-emerald-500">
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-emerald-400 font-mono mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  placeholder="Your name"
                />
              </div>
            
              <div>
                <label htmlFor="email" className="block text-emerald-400 font-mono mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-emerald-400 font-mono mb-2">Subject</label>
            <input
              type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
              className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-emerald-400 font-mono mb-2">Message</label>
            <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono resize-none"
                  placeholder="Your message here..."
                />
            </div>

              {remainingTime > 0 && (
                <div className="text-emerald-400 text-sm mt-2 text-center font-mono">
                  Please wait {Math.ceil(remainingTime / 1000)} seconds before submitting again
              </div>
            )}

              <motion.button
                type="submit"
                className={`w-full bg-emerald-500 text-white px-6 py-3 rounded-lg font-mono flex items-center justify-center space-x-2 ${
                  remainingTime > 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                whileHover={{ scale: remainingTime > 0 ? 1 : 1.02 }}
                whileTap={{ scale: remainingTime > 0 ? 1 : 0.98 }}
                disabled={remainingTime > 0}
              >
                <Send className="w-4 h-4" />
                <span>Send Message</span>
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

const DevSolbvrWebsite = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Suppress specific development messages and errors
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleLog = console.log;

    // Custom error handler
    console.error = (...args) => {
      const errorMsg = args[0]?.toString() || '';
      if (
        !errorMsg.includes('Permissions-Policy') &&
        !errorMsg.includes('TrustedScript') &&
        !errorMsg.includes('React') &&
        !errorMsg.includes('Content Security Policy') &&
        !errorMsg.includes('X-Frame-Options') &&
        !errorMsg.includes('injectLeap') &&
        !/^Warning:/.test(errorMsg) &&
        !errorMsg.includes('webpack_require')
      ) {
        originalConsoleError.apply(console, args);
      }
    };

    // Custom warning handler
    console.warn = (...args) => {
      const warnMsg = args[0]?.toString() || '';
      if (
        !warnMsg.includes('Permissions-Policy') &&
        !warnMsg.includes('TrustedScript') &&
        !warnMsg.includes('React') &&
        !warnMsg.includes('Content Security Policy') &&
        !warnMsg.includes('X-Frame-Options') &&
        !warnMsg.includes('injectLeap') &&
        !/^Warning:/.test(warnMsg)
      ) {
        originalConsoleWarn.apply(console, args);
      }
    };

    // Custom log handler
    console.log = (...args) => {
      const logMsg = args[0]?.toString() || '';
      if (
        !logMsg.includes('webpack') &&
        !logMsg.includes('TrustedScript') &&
        !logMsg.includes('Permissions-Policy') &&
        !logMsg.includes('injectLeap')
      ) {
        originalConsoleLog.apply(console, args);
      }
    };

    // 🦫 Console intro message with some degen vibes
    console.log(`%c
    🦫 SOLBEAVERS DEVS HAVE ENTERED THE CHAT 🦫
    ==========================================
    
    🚀 Sup Degens! Welcome to the dam! 
    
    🛠️ Your Web3 Development Squad:
    
    👨‍💻 SAMGAD - Backend Wizard & Blockchain Architect
       "Building backends so solid, they make diamond hands look weak!"
    
    🎨 FAWL3R - Frontend Artisan & UX Mastermind
       "Making UIs smoother than your favorite memecoins!"
    
    📢 OBZ - Marketing Degen & Community Builder
       "Spreading the word faster than a viral NFT drop!"
    
    👑 PEPERONCINO - The Master Orchestrator
       "Managing chaos like a pro since the ICO era!"
    
    🌐 Find us in the wild:
    Twitter: @thesolbeavers
    Discord: discord.gg/VGdgKP49Nh
    
    💎 Remember: WAGMI but only with proper development!
    `, 
    'font-family: monospace; color: #10B981; font-size: 12px;');

    // Cleanup function to restore original console methods
    return () => {
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.log = originalConsoleLog;
    };
  }, []);

  return (
    <div className="bg-black min-h-screen text-white">
      <MatrixRain />
      <Navbar />
      <Hero />
      <Services />
      <Solutions />
      <About />
      <ContactForm />
      <QuoteSection />
      <Footer />
      <style>{`
        html {
          scroll-behavior: smooth;
        }
        
        .matrix-rain {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          display: flex;
        }
        
        .matrix-column {
          display: flex;
          flex-direction: column;
          flex: 1;
          align-items: center;
          animation: rain 20s linear infinite;
          transform-style: preserve-3d;
        }
        
        .matrix-column span {
          animation: glow 1.5s linear infinite;
        }
        
        @keyframes rain {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        
        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default DevSolbvrWebsite; 